import { P2GAlert, P2GBox, P2GButton, P2GText } from '@parcel2go/core-ui';
import { QuoteError } from '~/lib/types/basket-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';

type BasketItemDetailsProps = {
  errors: QuoteError[];
  onResolve?: (action: string) => void;
  wrapper: (c: React.ReactNode) => React.ReactNode;
};

type ErrorType = {
  type: string;
  title: string;
  titleKey: string;
  detail: string;
  detailKey: string;
  action: string;
};

export function BasketError({ errors, wrapper, onResolve }: BasketItemDetailsProps) {
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);

  const visibleErrors = useMemo(() => {
    const output: ErrorType[] = [];

    errors.forEach((e) => {
      const type = `${e.type.split('/').pop()?.replace(/-/g, '_')}`.toUpperCase();

      let action = '';
      if (type.indexOf('ORIGIN') > -1 || e.field === 'email' || e.field === 'phone' || e.section === 'origin') {
        action = 'Origin';
      } else if (type.indexOf('DESTINATION') > -1 || e.section === 'destination') {
        action = 'Destination';
      } else if (type.indexOf('CUSTOMER') > -1 || e.section === 'account') {
        action = 'Account';
      } else {
        if (['Weight', 'Length', 'Height', 'Width'].some((v) => e.title?.indexOf(v) > -1) || type.includes('PARCEL')) {
          action = 'Parcels';
        }
      }

      output.push({
        type: e.type,
        detail: e.detail,
        detailKey: `ERROR_MESSAGE_${type}`.toUpperCase(),
        title: e.title,
        titleKey: `ERROR_TITLE_${type}`.toUpperCase(),
        action: action
      });
    });

    return output;
  }, [errors]);

  if (!visibleErrors || visibleErrors.length === 0) {
    return null;
  }

  return wrapper(
    visibleErrors.slice(0, 1).map((error) => (
      <P2GAlert key={error.titleKey + error.detail} type="error">
        <P2GBox display="flex" flexDirection="column" gap={1} textAlign="left">
          <P2GText variant="subtitle2" color="inherit">
            {t(error.titleKey, { defaultValue: error.title })}
          </P2GText>
          <P2GText variant="body1">{t(error.detailKey, { defaultValue: error.detail })}</P2GText>
        </P2GBox>
        {error.action && onResolve && (
          <P2GBox mt={3}>
            <P2GButton onClick={() => onResolve(error.action)}>
              {t('ERROR_RESOLVE_BUTTON', { defaultValue: 'Resolve' })}
            </P2GButton>
          </P2GBox>
        )}
      </P2GAlert>
    ))
  );
}
